import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { LiquidityPosition } from "@greenbook/client";
import { useDepth } from "../lib/queries.js";
import { fmtCents, fmtSize } from "../lib/format.js";
import { venueColorMap } from "../lib/venueColors.js";
import { mergeBook, VenueDot } from "./DepthPanel.js";
import { Loading } from "./ui.js";
import { LockedInline } from "./Gate.js";
import { SCOPES, useAccess } from "../lib/omauth.js";

const VISIBLE_LEVELS = 14;

/**
 * Two-sided orderbook for the current book slice: one ladder per side, side by
 * side, **cheapest price on top** (best fill first, worsening downward). The
 * combined line = best A + best B — under 100¢ the book can be greened.
 * Venue dots + per-level hover popups match the shared venue colors.
 */
export function DepthBook({ positions }: { positions: LiquidityPosition[] }) {
  const sides = positions.slice(0, 3); // 2 typical; 3-way books stack the third
  if (sides.length === 0) return null;
  return <DepthBookInner key={sides.map((p) => p.position_hash).join("|")} sides={sides} />;
}

function DepthBookInner({ sides }: { sides: LiquidityPosition[] }) {
  // One depth query per side — stable hook order via fixed-length mapping in
  // children (each SideLadder owns its query).
  const [bests, setBests] = useState<Record<string, number | null>>({});
  const reportBest = (hash: string, best: number | null) =>
    setBests((cur) => (cur[hash] === best ? cur : { ...cur, [hash]: best }));

  const combined = useMemo(() => {
    if (sides.length !== 2) return null;
    const a = bests[sides[0].position_hash];
    const b = bests[sides[1].position_hash];
    if (a == null || b == null) return null;
    return (a + b) * 100;
  }, [bests, sides]);

  return (
    <section className="h-full bg-panel">
      <div className="flex items-center gap-2 border-b border-border-2 px-4 py-2.5 lg:px-3 lg:py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-amber">orderbook</h3>
        {combined != null && (
          <span
            className="nums text-xs lg:text-[11px]"
            title="best price side A + best price side B — under 100¢ means both sides can be bought for a guaranteed green book (before fees)"
          >
            <span className="text-faint">combined </span>
            <span className={combined < 100 ? "font-semibold text-up" : "text-dim"}>
              {combined.toFixed(1)}¢
            </span>
            {combined < 100 && <span className="ml-1 text-[10px] text-up">▲ green</span>}
          </span>
        )}
      </div>

      <div className={`grid ${sides.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-px bg-border`}>
        {sides.map((pos) => (
          <SideLadder key={pos.position_hash} position={pos} onBest={reportBest} />
        ))}
      </div>
    </section>
  );
}

function sideLabel(pos: LiquidityPosition): string {
  const side = pos.side_key ? pos.side_key.toUpperCase() : null;
  if (pos.participant_name && side) return `${pos.participant_name} ${side}`;
  return side ?? pos.title ?? "side";
}

/** Best live top-of-book from the liquidity feed — the trustworthy reference. */
function liqBest(position: LiquidityPosition): number | null {
  let best: number | null = null;
  for (const pl of position.partner_liquidities ?? []) {
    if ((pl.price ?? 0) > 0 && (pl.available ?? 0) > 0) {
      if (best === null || pl.price! < best) best = pl.price!;
    }
  }
  return best;
}

function SideLadder({
  position,
  onBest,
}: {
  position: LiquidityPosition;
  onBest: (hash: string, best: number | null) => void;
}) {
  const gateOk = useAccess(SCOPES.depth).ok;
  const depthQ = useDepth(position.position_hash);
  const [expanded, setExpanded] = useState(false);
  const partners = depthQ.data?.partners ?? [];

  const colors = useMemo(
    () => venueColorMap(partners.map((p) => p.partner_name ?? p.partner_id)),
    [partners],
  );

  // Cheapest first: best fill on top, worsening downward.
  const book = useMemo(() => {
    const merged = mergeBook(partners);
    const rows = [...merged.rows].sort((a, b) => a.price - b.price);
    // recompute cum walking DOWN from the best price
    let running = 0;
    const cumRows = rows.map((r) => {
      running += r.available;
      return { ...r, cum: running };
    });
    return { rows: cumRows, maxCum: running || 1 };
  }, [partners]);

  const best = book.rows[0]?.price ?? null;

  // Upstream bug guard (see FRICTION: depth returns the OPPOSITE side's book
  // for moneyline home positions): if the ladder's best is far from this
  // position's own liquidity top-of-book but ≈ its complement, the ladder is
  // the wrong side's book. Flag it and keep it out of the combined readout.
  const reference = liqBest(position);
  const flipped =
    best != null &&
    reference != null &&
    Math.abs(best - reference) > 0.05 &&
    Math.abs(1 - best - reference) <= 0.05;

  // publish best price up for the combined readout (post-render); a flipped
  // ladder reports null so no fake "green" combined is ever shown.
  useEffect(() => {
    onBest(position.position_hash, flipped ? null : best);
  }, [position.position_hash, best, flipped, onBest]);

  const visible = expanded ? book.rows : book.rows.slice(0, VISIBLE_LEVELS);

  return (
    <div className="min-w-0 bg-panel">
      <div className="flex items-baseline gap-1.5 border-b border-border px-3 py-2 lg:px-2 lg:py-1.5">
        <span
          className="min-w-0 break-words text-xs font-semibold text-ink lg:text-[11px]"
          title={position.title ?? position.position_hash}
        >
          {sideLabel(position)}
        </span>
        {flipped ? (
          <span
            className="ml-auto rounded border border-down/60 bg-down/15 px-1 text-[9px] uppercase text-down"
            title={`Upstream returned the opposite side's book (depth best ${fmtCents(best)} vs this side's top-of-book ${fmtCents(reference)}). Tracked in FRICTION.md — ladder below is NOT this side's book.`}
          >
            wrong book
          </span>
        ) : (
          <span className="nums ml-auto text-xs text-amber lg:text-[11px]">{fmtCents(best)}</span>
        )}
        <Link
          to={`/depth/${encodeURIComponent(position.position_hash)}`}
          className="-m-1.5 shrink-0 p-1.5 text-[10px] text-faint hover:text-amber lg:m-0 lg:p-0"
          title="full-page depth"
        >
          ⤢
        </Link>
      </div>

      {!gateOk ? (
        <LockedInline scope={SCOPES.depth} />
      ) : depthQ.isLoading ? (
        <Loading label="…" />
      ) : book.rows.length === 0 ? (
        <div className="px-2 py-4 text-center text-[10px] text-faint">no book</div>
      ) : (
        <div className={flipped ? "opacity-40" : undefined}>
          <div className="grid grid-cols-[3.6rem_1fr_auto] gap-1 border-b border-border px-3 py-1.5 text-[10px] uppercase text-faint lg:grid-cols-[3.2rem_1fr_auto] lg:px-2 lg:py-1 lg:text-[9px]">
            <span className="text-right">price</span>
            <span className="text-right">size</span>
            <span className="pl-1">src</span>
          </div>
          {visible.map((r, i) => (
            <Level
              key={r.price}
              row={r}
              maxCum={book.maxCum}
              colors={colors}
              isBest={i === 0 && !expanded ? true : r.price === best}
            />
          ))}
          {book.rows.length > VISIBLE_LEVELS && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-full border-t border-border px-2 py-2 text-center text-[11px] text-dim hover:text-amber lg:py-1 lg:text-[9px]"
            >
              {expanded ? "less" : `+${book.rows.length - VISIBLE_LEVELS}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Level({
  row,
  maxCum,
  colors,
  isBest,
}: {
  row: { price: number; available: number; cum: number; byVenue: [string, number][] };
  maxCum: number;
  colors: Map<string, string>;
  isBest: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative grid grid-cols-[3.6rem_1fr_auto] items-center gap-1 px-3 py-1.5 text-[13px] hover:bg-raised/60 lg:grid-cols-[3.2rem_1fr_auto] lg:px-2 lg:py-0.5 lg:text-[11px]"
    >
      <span
        className="absolute inset-y-0 left-0 bg-amber/10"
        style={{ width: `${(row.cum / maxCum) * 100}%` }}
      />
      <span
        className={`nums relative z-10 text-right ${isBest ? "font-semibold text-amber" : "text-ink"}`}
      >
        {fmtCents(row.price)}
      </span>
      <span className="nums relative z-10 text-right text-dim">{fmtSize(row.available)}</span>
      <span className="relative z-10 flex items-center gap-0.5 pl-1">
        {row.byVenue.slice(0, 3).map(([name]) => (
          <VenueDot key={name} color={colors.get(name)} size={6} />
        ))}
        {row.byVenue.length > 3 && (
          <span className="text-[9px] text-faint">+{row.byVenue.length - 3}</span>
        )}
      </span>

      {hover && (
        <div className="absolute right-1 top-full z-20 mt-0.5 min-w-[11rem] rounded border border-border-2 bg-panel px-2 py-1.5 shadow-lg">
          <div className="mb-1 flex justify-between text-[10px] uppercase text-faint">
            <span>{fmtCents(row.price)}</span>
            <span className="nums">
              {fmtSize(row.available)} · cum {fmtSize(row.cum)}
            </span>
          </div>
          {row.byVenue.map(([name, amt]) => (
            <div key={name} className="flex items-center gap-1.5 py-0.5 text-[11px]">
              <VenueDot color={colors.get(name)} />
              <span className="text-dim">{name}</span>
              <span className="nums ml-auto text-ink">{fmtSize(amt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
